import React, { useCallback, useEffect, useState } from 'react';
import { Search, X, ExternalLink, UserPlus, Send, Pause, Play, Check, CheckCircle2, XCircle, AlertTriangle, Hourglass } from 'lucide-react';
import { api, inputCls, Button, Notice, StatusBadge, AGENT_LABEL, KIND_LABEL, formatWhen, timeAgo, describeTerms } from './ui';

/** Every affiliate the team is working, grouped by where it stands. */

const GROUPS = [
  { key: 'attention', label: 'Needs you' },
  { key: 'talking', label: 'In conversation' },
  { key: 'held', label: 'On hold' },
  { key: 'outreach', label: 'Contacted' },
  { key: 'pipeline', label: 'Up next' },
  { key: 'closed', label: 'Won / lost' },
  { key: 'dropped', label: 'Dropped' },
  { key: '', label: 'All' }
];

const list = (v) => (Array.isArray(v) ? v : []);

const Message = ({ m, onSendNow, sending }) => {
  const inbound = m.direction === 'inbound';
  const draft = ['pending_approval', 'scheduled', 'sending'].includes(m.status);
  const waiting = ['pending_approval', 'scheduled'].includes(m.status);
  const dead = ['discarded', 'failed'].includes(m.status);
  return (
    <div className={`flex ${inbound ? 'justify-start' : 'justify-end'}`}>
      <div className={`max-w-[85%] rounded-xl border p-3 text-sm ${inbound
        ? 'border-gray-200 bg-white dark:border-slate-700 dark:bg-slate-900'
        : draft ? 'border-dashed border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/30'
          : dead ? 'border-gray-200 bg-gray-50 opacity-60 dark:border-slate-800 dark:bg-slate-950'
            : 'border-blue-200 bg-blue-50 dark:border-blue-900 dark:bg-blue-950/40'}`}>
        <div className="mb-1 flex flex-wrap items-center gap-2 text-xs text-gray-500 dark:text-slate-400">
          <span className="font-medium text-gray-700 dark:text-slate-300">{inbound ? m.from_email : AGENT_LABEL[m.author] || m.author}</span>
          <span>{KIND_LABEL[m.kind] || m.kind}</span>
          {m.intent && <span>· {m.intent.replace(/_/g, ' ')}</span>}
          <span>· {m.status === 'sent' ? formatWhen(m.sent_at) : m.status === 'received' ? formatWhen(m.received_at) : `${m.status.replace('_', ' ')}${m.send_at && draft ? `, ${formatWhen(m.send_at)}` : ''}`}</span>
          {m.edited && <span className="text-violet-600 dark:text-violet-300">· edited by a person</span>}
        </div>
        {m.subject && !inbound && m.kind === 'first_touch' && <div className="mb-1 font-medium text-gray-800 dark:text-slate-200">{m.subject}</div>}
        <div className="whitespace-pre-wrap text-gray-800 dark:text-slate-200">{m.body_text}</div>
        {(m.error || m.approval_reason) && <div className="mt-1 text-xs text-amber-700 dark:text-amber-300">{m.error || m.approval_reason}</div>}
        {waiting && onSendNow && (
          <div className="mt-2">
            <Button variant="success" icon={Send} busy={sending} onClick={() => onSendNow(m)}
              title="Sends this email now, as written, outside the send window and the delay">Send now</Button>
          </div>
        )}
      </div>
    </div>
  );
};

const FitBar = ({ label, value }) => (
  <div>
    <div className="flex justify-between text-xs text-gray-500 dark:text-slate-400"><span>{label}</span><span>{value}</span></div>
    <div className="mt-0.5 h-1.5 rounded-full bg-gray-100 dark:bg-slate-800">
      <div className={`h-1.5 rounded-full ${value >= 70 ? 'bg-green-500' : value >= 40 ? 'bg-amber-500' : 'bg-red-500'}`} style={{ width: `${Math.max(2, Math.min(100, value || 0))}%` }} />
    </div>
  </div>
);

const pct = (v) => (v == null ? null : `${Math.round(v * 100)}%`);

/** How the Vetter judged relevance: domain rank data (top GEOs, top keywords) and the site. */
const Relevance = ({ rel }) => {
  const s = rel.signals || {};
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 text-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="mb-3 flex flex-wrap items-baseline gap-2">
        <span className="font-semibold text-gray-900 dark:text-slate-100">Relevance check</span>
        <span className="text-xs text-gray-500 dark:text-slate-400">GEOs: {rel.sources?.geos || 'none'} · keywords: {rel.sources?.keywords || 'none'}{rel.checked_at ? ` · ${timeAgo(rel.checked_at)}` : ''}</span>
      </div>
      {rel.fit && (
        <div className="mb-3 grid gap-3 sm:grid-cols-3">
          <FitBar label="Markets" value={rel.fit.markets} />
          <FitBar label="Keywords" value={rel.fit.keywords} />
          <FitBar label="Site" value={rel.fit.site} />
        </div>
      )}
      <dl className="space-y-1 text-gray-700 dark:text-slate-300">
        {rel.site_summary && <div><dt className="inline text-gray-500">The site: </dt><dd className="inline">{rel.site_summary}</dd></div>}
        {list(rel.top_markets).length > 0 && (
          <div><dt className="inline text-gray-500">Top GEOs: </dt><dd className="inline">{rel.top_markets.map((m) => `${m.market} ${Number(m.traffic || 0).toLocaleString()}`).join(', ')}{s.target_market_share != null ? ` (${pct(s.target_market_share)} in target markets)` : ''}</dd></div>
        )}
        {list(rel.top_keywords).length > 0 && (
          <div><dt className="inline text-gray-500">Top keywords: </dt><dd className="inline">{rel.top_keywords.join(', ')}</dd></div>
        )}
        {s.igaming_keyword_share != null && (
          <div><dt className="inline text-gray-500">Keyword fit: </dt><dd className="inline">{pct(s.igaming_keyword_share)} iGaming terms{Object.entries(s.vertical_keyword_share || {}).map(([v, x]) => `, ${pct(x)} ${v}`).join('')}{s.bonus_hunting_share ? `, ${pct(s.bonus_hunting_share)} bonus hunting` : ''}</dd></div>
        )}
        {list(s.competitor_brand_keywords).length > 0 && <div><dt className="inline text-gray-500">Competitor keywords: </dt><dd className="inline">{s.competitor_brand_keywords.join(', ')}</dd></div>}
        {list(s.shared_top_markets_with_brand).length > 0 && <div><dt className="inline text-gray-500">Shares the brand's top markets: </dt><dd className="inline">{s.shared_top_markets_with_brand.join(', ')}</dd></div>}
        {rel.site && (
          <div><dt className="inline text-gray-500">Home page: </dt><dd className="inline">{[rel.site.title, list(rel.site.site_types).join(', '), list(rel.site.regulators).length ? `mentions ${rel.site.regulators.join(', ')}` : null].filter(Boolean).join(' · ')}</dd></div>
        )}
      </dl>
    </div>
  );
};

export const OpportunityDrawer = ({ id, onClose, onChanged }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);
  const [newContact, setNewContact] = useState('');
  const [reply, setReply] = useState('');

  const load = useCallback(async () => {
    try {
      setData(await api(`/opportunities/${id}`));
      setError(null);
    } catch (err) {
      setError(err.message);
    }
  }, [id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  const act = async (key, fn) => {
    setBusy(key);
    setError(null);
    try {
      await fn();
      await load();
      onChanged && onChanged();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(null);
    }
  };
  const action = (name, extra = {}) => act(name, () => api(`/opportunities/${id}/action`, { method: 'POST', body: { action: name, ...extra } }));
  const sendNow = (m) => act(`send-${m.id}`, async () => {
    const res = await api(`/messages/${m.id}/approve`, { method: 'POST', body: { send_now: true } });
    if (res?.outcome && res.outcome !== 'sent') throw new Error(`Not sent (${res.outcome}). The note on the email says why.`);
  });

  if (!data) {
    return (
      <div className="fixed inset-0 z-40 flex justify-end bg-black/30" onClick={onClose}>
        <div className="h-full w-full max-w-3xl bg-gray-50 p-6 dark:bg-slate-950">{error ? <Notice tone="error">{error}</Notice> : 'Loading...'}</div>
      </div>
    );
  }

  const o = data.opportunity;
  const terms = o.deal_terms || {};
  const snap = o.snapshot || {};
  const rel = snap.relevance || null;
  const canApprove = ['review', 'rejected', 'no_contact', 'scouted'].includes(o.status);
  const canPause = !['paused', 'won', 'lost', 'opted_out', 'rejected'].includes(o.status);
  const canResume = ['paused', 'needs_human'].includes(o.status);
  const closed = ['won', 'lost', 'opted_out'].includes(o.status);
  const onHold = o.status === 'on_hold';

  return (
    <div className="fixed inset-0 z-40 flex justify-end bg-black/30" onClick={onClose}>
      <div className="h-full w-full max-w-3xl overflow-y-auto bg-gray-50 dark:bg-slate-950" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 z-10 flex items-center gap-3 border-b border-gray-200 bg-white px-5 py-3 dark:border-slate-800 dark:bg-slate-900">
          <a href={`https://${o.domain}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-lg font-semibold text-gray-900 hover:underline dark:text-slate-100">
            {o.domain} <ExternalLink className="h-4 w-4" />
          </a>
          <StatusBadge status={o.status} />
          <button type="button" onClick={onClose} className="ml-auto text-gray-400 hover:text-gray-700 dark:hover:text-slate-200"><X className="h-5 w-5" /></button>
        </div>

        <div className="space-y-4 p-5">
          {error && <Notice tone="error">{error}</Notice>}
          {o.human_reason && ['needs_human', 'review', 'paused'].includes(o.status) && (
            <Notice tone="warn"><div className="flex gap-2"><AlertTriangle className="mt-0.5 h-4 w-4 flex-none" />{o.human_reason}</div></Notice>
          )}
          {onHold && (
            <Notice tone="info">
              <div className="flex gap-2">
                <Hourglass className="mt-0.5 h-4 w-4 flex-none" />
                <div>
                  <div className="font-medium">On hold with the Stop agent{o.hold_until ? `. Next step: ${formatWhen(o.hold_until)} (${timeAgo(o.hold_until)})` : ''}</div>
                  {o.hold_reason && <div className="mt-0.5">{o.hold_reason}</div>}
                  <div className="mt-0.5 text-xs opacity-80">Check-ins written so far: {o.hold_checkins || 0}. A reply from them picks the negotiation up again.</div>
                </div>
              </div>
            </Notice>
          )}

          <div className="flex flex-wrap gap-2">
            {onHold && <Button variant="primary" icon={Send} busy={busy === 'checkin_now'} onClick={() => action('checkin_now')} title="The Stop agent writes the check-in on its next round">Check in now</Button>}
            {onHold && <Button icon={Hourglass} busy={busy === 'hold_longer'} onClick={() => action('hold_longer')}>Give them longer</Button>}
            {canApprove && <Button variant="primary" icon={Check} busy={busy === 'approve'} onClick={() => action('approve')}>Approve for outreach</Button>}
            {canApprove && o.status !== 'rejected' && <Button variant="danger" icon={X} busy={busy === 'reject'} onClick={() => action('reject')}>Reject</Button>}
            {canPause && <Button icon={Pause} busy={busy === 'pause'} onClick={() => action('pause')} title="The agents stop; you handle it">Take over</Button>}
            {canResume && <Button icon={Play} busy={busy === 'resume'} onClick={() => action('resume')}>Hand back to the agents</Button>}
            {!closed && o.last_inbound_at && <Button variant="success" icon={CheckCircle2} busy={busy === 'mark_won'} onClick={() => action('mark_won')}>Mark won</Button>}
            {!closed && o.last_outbound_at && <Button variant="danger" icon={XCircle} busy={busy === 'mark_lost'} onClick={() => action('mark_lost')}>Mark lost</Button>}
          </div>

          <div className="grid gap-3 md:grid-cols-2">
            <div className="rounded-xl border border-gray-200 bg-white p-4 text-sm dark:border-slate-800 dark:bg-slate-900">
              <div className="mb-2 font-semibold text-gray-900 dark:text-slate-100">What the agents found</div>
              <dl className="space-y-1 text-gray-700 dark:text-slate-300">
                {o.meta?.direct && <div className="text-blue-700 dark:text-blue-300">Handed over by a person{o.meta.note ? `: ${o.meta.note}` : ''}</div>}
                <div><dt className="inline text-gray-500">Traffic: </dt><dd className="inline">{(snap.organic_traffic || 0).toLocaleString()} organic, {(snap.target_market_traffic || 0).toLocaleString()} in target markets</dd></div>
                {o.recommended_geo && <div><dt className="inline text-gray-500">Lead market: </dt><dd className="inline">{o.recommended_geo}</dd></div>}
                {o.scout_reason && !o.meta?.direct && <div><dt className="inline text-gray-500">Scout ({o.scout_score}): </dt><dd className="inline">{o.scout_reason}</dd></div>}
                {list(o.vet_reasons).length > 0 && <div><dt className="inline text-gray-500">Vetter{o.vet_score != null ? ` (${o.vet_score})` : ''}: </dt><dd className="inline">{list(o.vet_reasons).join(' ')}</dd></div>}
                {list(o.red_flags).length > 0 && <div className="text-red-600 dark:text-red-400">Red flags: {list(o.red_flags).join('; ')}</div>}
                {o.personalization && <div><dt className="inline text-gray-500">Hook: </dt><dd className="inline italic">{o.personalization}</dd></div>}
              </dl>
            </div>
            <div className="rounded-xl border border-gray-200 bg-white p-4 text-sm dark:border-slate-800 dark:bg-slate-900">
              <div className="mb-2 font-semibold text-gray-900 dark:text-slate-100">Deal</div>
              {data.deal && <div className="mb-1 text-gray-700 dark:text-slate-300"><span className="text-gray-500">Saved deal: </span>{data.deal.name}{data.deal.opening_line ? ` (opens at ${data.deal.opening_line})` : ''}{o.meta?.led_with_offer ? ', named in the first email' : ''}</div>}
              {data.mailbox && <div className="mb-1 text-gray-700 dark:text-slate-300"><span className="text-gray-500">Mailbox: </span>{data.mailbox}</div>}
              {terms.their_ask || terms.our_offer ? (
                <dl className="space-y-1 text-gray-700 dark:text-slate-300">
                  {terms.stage && <div><dt className="inline text-gray-500">Stage: </dt><dd className="inline">{terms.stage.replace('_', ' ')}</dd></div>}
                  {terms.their_ask && <div><dt className="inline text-gray-500">They ask: </dt><dd className="inline">{describeTerms(terms.their_ask)}</dd></div>}
                  {terms.our_offer && <div><dt className="inline text-gray-500">Our offer: </dt><dd className="inline">{describeTerms(terms.our_offer)}</dd></div>}
                </dl>
              ) : <div className="text-gray-500 dark:text-slate-400">No terms discussed yet.</div>}
              {o.stage_note && <div className="mt-2 text-xs italic text-gray-500 dark:text-slate-400">{o.stage_note}</div>}
              {o.next_action_at && <div className="mt-2 text-xs text-gray-500 dark:text-slate-400">Next step {timeAgo(o.next_action_at)}{o.followup_step ? ` (follow-ups sent: ${o.followup_step})` : ''}</div>}
            </div>
          </div>

          {rel && <Relevance rel={rel} />}

          <div className="rounded-xl border border-gray-200 bg-white p-4 text-sm dark:border-slate-800 dark:bg-slate-900">
            <div className="mb-2 font-semibold text-gray-900 dark:text-slate-100">Contacts</div>
            {!data.contacts.length && <div className="text-gray-500">None yet.</div>}
            <ul className="space-y-1">
              {data.contacts.map((c) => (
                <li key={c.id} className="flex flex-wrap items-center gap-2">
                  <span className={`font-mono text-xs ${['bounced', 'opted_out'].includes(c.status) ? 'text-gray-400 line-through' : 'text-gray-800 dark:text-slate-200'}`}>{c.email}</span>
                  {c.name && <span className="text-gray-600 dark:text-slate-400">{c.name}{c.role ? `, ${c.role}` : ''}</span>}
                  <span className="text-xs text-gray-400">{c.status}{c.id === o.contact_id ? ' · current' : ''} · {c.source}</span>
                  {c.bounce_reason && <span className="text-xs text-orange-600">{c.bounce_reason}</span>}
                </li>
              ))}
            </ul>
            <div className="mt-3 flex gap-2">
              <input className={inputCls} placeholder="Add an address" value={newContact} onChange={(e) => setNewContact(e.target.value)} />
              <Button icon={UserPlus} busy={busy === 'contact'} disabled={!newContact.trim()}
                onClick={() => act('contact', async () => { await api(`/opportunities/${id}/contacts`, { method: 'POST', body: { email: newContact } }); setNewContact(''); })}>Add</Button>
            </div>
          </div>

          <div className="space-y-2">
            <div className="font-semibold text-gray-900 dark:text-slate-100">Conversation</div>
            {!data.messages.length && <div className="text-sm text-gray-500">No emails yet.</div>}
            {data.messages.map((m) => <Message key={m.id} m={m} onSendNow={sendNow} sending={busy === `send-${m.id}`} />)}
          </div>

          {o.status !== 'opted_out' && (
            <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
              <div className="mb-2 text-sm font-semibold text-gray-900 dark:text-slate-100">Write the next email yourself</div>
              <textarea rows={5} className={inputCls} value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Goes in the same thread, from the team mailbox, with the sender's signature. Any waiting draft is dropped." />
              <div className="mt-2">
                <Button variant="primary" icon={Send} busy={busy === 'reply'} disabled={!reply.trim()}
                  onClick={() => act('reply', async () => { await api(`/opportunities/${id}/reply`, { method: 'POST', body: { body: reply } }); setReply(''); })}>Send</Button>
              </div>
            </div>
          )}

          <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
            <div className="mb-2 text-sm font-semibold text-gray-900 dark:text-slate-100">Timeline</div>
            <ul className="space-y-1 text-xs">
              {data.events.map((e) => (
                <li key={e.id} className="flex gap-2 text-gray-600 dark:text-slate-400">
                  <span className="w-36 flex-none text-gray-400">{formatWhen(e.created_at)}</span>
                  <span className="w-28 flex-none font-medium text-gray-700 dark:text-slate-300">{AGENT_LABEL[e.agent] || e.agent}</span>
                  <span>{e.event.replace(/_/g, ' ')}{e.detail ? `: ${e.detail}` : ''}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

const Pipeline = ({ team, onOpen, refreshKey }) => {
  const [group, setGroup] = useState('attention');
  const [q, setQ] = useState('');
  const [data, setData] = useState({ rows: [], counts: {}, groups: {} });
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (group) params.set('group', group);
      if (q) params.set('q', q);
      setData(await api(`/teams/${team.id}/opportunities?${params}`));
      setError(null);
    } catch (err) {
      setError(err.message);
    }
  }, [team.id, group, q]);

  useEffect(() => { load(); }, [load, refreshKey]);
  useEffect(() => {
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  const countFor = (g) => (g ? (data.groups?.[g] || []).reduce((s, st) => s + (data.counts?.[st] || 0), 0)
    : Object.values(data.counts || {}).reduce((s, n) => s + n, 0));

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        {GROUPS.map((g) => (
          <button key={g.key || 'all'} type="button" onClick={() => setGroup(g.key)}
            className={`rounded-full border px-3 py-1 text-sm ${group === g.key
              ? 'border-blue-500 bg-blue-50 text-blue-700 dark:border-blue-400 dark:bg-blue-900/30 dark:text-blue-200'
              : 'border-gray-200 bg-white text-gray-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300'}`}>
            {g.label} <span className="ml-1 text-xs text-gray-500 dark:text-slate-400">{countFor(g.key)}</span>
          </button>
        ))}
        <div className="relative ml-auto">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
          <input className={`${inputCls} w-56 pl-8`} placeholder="Find a domain" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
      </div>
      {error && <Notice tone="error">{error}</Notice>}
      <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white dark:border-slate-800 dark:bg-slate-900">
        <table className="w-full text-left text-sm">
          <thead className="border-b border-gray-200 text-xs uppercase text-gray-500 dark:border-slate-800 dark:text-slate-400">
            <tr>
              <th className="px-4 py-2">Affiliate</th>
              <th className="px-4 py-2">Status</th>
              <th className="px-4 py-2">Score</th>
              <th className="px-4 py-2">Market traffic</th>
              <th className="px-4 py-2">Contact</th>
              <th className="px-4 py-2">Where it stands</th>
              <th className="px-4 py-2">Updated</th>
            </tr>
          </thead>
          <tbody>
            {!data.rows.length && (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-gray-500 dark:text-slate-400">Nothing here yet.</td></tr>
            )}
            {data.rows.map((o) => (
              <tr key={o.id} onClick={() => onOpen(o.id)} className="cursor-pointer border-b border-gray-100 hover:bg-gray-50 dark:border-slate-800 dark:hover:bg-slate-800/50">
                <td className="px-4 py-2">
                  <div className="font-medium text-gray-900 dark:text-slate-100">{o.domain}</div>
                  <div className="text-xs text-gray-500">
                    {o.recommended_geo}
                    {o.direct && <span className="ml-1 rounded-full bg-blue-50 px-1.5 text-blue-700 dark:bg-blue-900/40 dark:text-blue-200">handed over</span>}
                  </div>
                </td>
                <td className="px-4 py-2">
                  <div className="flex items-center gap-1.5"><StatusBadge status={o.status} />{o.pending > 0 && <span className="rounded-full bg-amber-500 px-1.5 text-xs text-white" title="Draft waiting for approval">{o.pending}</span>}</div>
                </td>
                <td className="px-4 py-2 text-gray-700 dark:text-slate-300">{o.vet_score ?? o.scout_score ?? '—'}</td>
                <td className="px-4 py-2 text-gray-700 dark:text-slate-300">{o.target_market_traffic != null ? o.target_market_traffic.toLocaleString() : '—'}</td>
                <td className="px-4 py-2 text-xs text-gray-600 dark:text-slate-400">{o.contact_email || '—'}</td>
                <td className="max-w-xs px-4 py-2 text-xs text-gray-600 dark:text-slate-400">
                  <div className="line-clamp-2">
                    {o.human_reason && ['needs_human', 'review', 'paused'].includes(o.status) ? o.human_reason
                      : o.deal_terms?.our_offer ? `Offer: ${describeTerms(o.deal_terms.our_offer)}`
                        : o.next_action_at ? `Next step ${timeAgo(o.next_action_at)}` : o.last_error || ''}
                  </div>
                </td>
                <td className="px-4 py-2 text-xs text-gray-500">{timeAgo(o.updated_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Pipeline;
